/**
 * @file
 * Modern SRP filter layout (ACF "Filter Layout" = Modern).
 *
 * Amazon-style faceted filters on top of the inventory plugin's search engine:
 * checkbox lists sorted by popularity with See more, price bands with a
 * histogram slider, year chips, body style icons, a Deals & savings filter,
 * filters that appear once their parent is chosen, quick-filter pills and
 * removable chips above the results, and a two-pane filter sheet on mobile.
 */

import $ from 'jquery';
import { TagManager } from '@dealersites/tag-manager/tag-manager';
import { InventorySearchEvents } from '@dealersites/inventory/events';

import { FilterControl, pendingParams } from './controls.js';
import {
  getUrlParams,
  escapeHtml,
  formatNumber,
  LOCKED_PARAMS,
  splitList,
  sprintf,
} from './utils.js';
import { formatPrice } from '../../utilities.js';

// Parameters that change the order or page of results but not the counts.
const COUNT_IGNORED_PARAMS = ['sort', 'page', 'paged', 'offset', 'limit'];

const root = document.querySelector('.srp-modern');
const dspFilters = document.getElementById('dsp-filters');

if (root && dspFilters) {
  const panel = root.querySelector('#srp-filter-panel');
  const i18n = JSON.parse(panel?.dataset.i18n ?? '{}');
  const activeFilters = root.querySelector('#srp-active-filters');
  const quickFilters = root.querySelector('.js-srp-quick-pills');
  const quickRow = quickFilters?.closest('.srp-quick');
  const groupNav = panel?.querySelector('.srp-filter-panel__nav');
  const resultsStatus = root.querySelector('#srp-results-status');
  const controls = new Map();

  let inventoryData = parseJson(dspFilters.dataset.inventory);
  let activeGroup = null;
  let hasSearched = false;
  let dealCountRequest = null;
  let dealCountQuery = '';

  /**
   * Values pinned by a WP custom filter apply before the first search, but only
   * reach the URL once the plugin has run a search. From then on the URL is the
   * only source, so a value the visitor clears doesn't come back.
   */
  function getParams() {
    const params = getUrlParams();
    if (hasSearched) {
      return params;
    }
    const selected = parseJson(dspFilters.dataset.selected);
    Object.entries(selected).forEach(([key, value]) => {
      if (!LOCKED_PARAMS.includes(key) && params[key] === undefined && value !== '') {
        params[key] = Array.isArray(value) ? value.join(',') : String(value);
      }
    });
    return params;
  }

  /**
   * Vehicles the current search found, from the results markup.
   */
  function resultTotal() {
    const total = Number.parseInt(
      root.querySelector('.vehicle-results-counts')?.dataset.total ?? '',
      10,
    );
    return Number.isNaN(total) ? null : total;
  }

  function renderAll() {
    const params = { ...getParams(), ...pendingParams() };
    const total = resultTotal();
    controls.forEach((control) => {
      control.resultTotal = total;
      control.render(inventoryData, params);
    });
    renderActiveFilters(params);
    renderQuickFilters(params);
    renderGroupNav();
    fetchDealCounts(params);
    controls.forEach((control) => control.afterSearch());
  }

  /*
  |--------------------------------------------------------------------------
  | Active filter chips
  |--------------------------------------------------------------------------
  */
  function renderActiveFilters(params) {
    const chips = [];
    controls.forEach((control) => {
      control.chips().forEach((chip) => chips.push({ ...chip, group: control.label }));
    });

    if (params.title) {
      chips.push({
        group: i18n.keyword,
        label: `“${params.title}”`,
        remove: () => {
          const title = document.getElementById('dsp_title');
          if (title) {
            title.value = '';
            title.dispatchEvent(new Event('input', { bubbles: true }));
          }
        },
      });
    }

    root.querySelectorAll('.js-srp-active-count').forEach((badge) => {
      badge.textContent = chips.length ? String(chips.length) : '';
      badge.hidden = !chips.length;
    });
    root.classList.toggle('has-active-filters', chips.length > 0);

    if (!activeFilters) {
      return;
    }

    if (!chips.length) {
      activeFilters.hidden = true;
      activeFilters.innerHTML = '';
      return;
    }

    const list = chips
      .map(
        (chip, index) => `<li>
          <button type="button" class="srp-active-filters__chip" data-chip="${index}"
            aria-label="${escapeHtml(sprintf(i18n.remove, `${chip.group}: ${chip.label}`))}">
            <span class="srp-active-filters__x" aria-hidden="true"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M4 4l8 8M12 4l-8 8"/></svg></span>
            <span>${escapeHtml(chip.group ? `${chip.group}: ${chip.label}` : chip.label)}</span>
          </button>
        </li>`,
      )
      .join('');

    activeFilters.innerHTML = `<ul class="srp-active-filters__list" role="list">${list}
        <li><a class="srp-active-filters__clear" href="${escapeHtml(resetUrl())}">${escapeHtml(i18n.clearAll)}</a></li>
      </ul>`;
    activeFilters.hidden = false;

    activeFilters.querySelectorAll('[data-chip]').forEach((button) => {
      button.addEventListener('click', () => {
        button.disabled = true;
        chips[Number(button.dataset.chip)].remove();
      });
    });
  }

  function resetUrl() {
    const url = new URL(window.location);
    const lang = url.searchParams.get('lang');
    url.search = '';
    if (lang) {
      url.searchParams.set('lang', lang);
    }
    return url.toString();
  }

  /*
  |--------------------------------------------------------------------------
  | Quick filters
  |--------------------------------------------------------------------------
  |
  | One-tap pills above the results, built from the filters already on the
  | page. Each pill toggles a value in its filter, so the sidebar, chips and
  | URL all stay in step.
  |
  */
  function listPill(control, option) {
    const selected = control.getSelected();
    const active = selected.includes(option.key);
    return {
      label: option.label,
      count: option.count,
      active,
      toggle: () => {
        const next = active
          ? selected.filter((value) => value !== option.key)
          : [...selected, option.key];
        control.emit({ value: next.join(',') });
      },
    };
  }

  function rangePill(control, label, count, min, max) {
    const params = control.params;
    const active =
      String(params[`${control.param}_min`] ?? '') === String(min ?? '') &&
      String(params[`${control.param}_max`] ?? '') === String(max ?? '');
    return {
      label,
      count,
      active,
      toggle: () => control.emit(active ? { min: null, max: null } : { min, max }),
    };
  }

  function quickFilterPills(params) {
    const pills = [];
    const visible = (id) => {
      const control = controls.get(id);
      return control && !control.section?.hidden ? control : null;
    };
    const options = (control) =>
      control
        .getOptions()
        .filter((option) => option.doc_count > 0)
        .map((option) => ({
          key: String(option.slug ?? option.id ?? option.name),
          label: control.labelFor(String(option.slug ?? option.id ?? option.name)),
          count: control.showCounts() ? option.doc_count : undefined,
        }));

    const deals = visible('deals');
    if (deals && (deals.dealCounts.on_sale > 0 || params.on_sale)) {
      const active = Boolean(params.on_sale);
      pills.push({
        label: i18n.salePrice,
        count: deals.dealCounts.on_sale,
        active,
        toggle: () => deals.emit({ param: 'on_sale', value: active ? '' : 'true' }),
      });
    }

    const price = visible('price');
    const [firstBand] = price?.getPriceBands() ?? [];
    if (firstBand?.max) {
      pills.push(
        rangePill(
          price,
          sprintf(i18n.under, formatPrice(firstBand.max)),
          firstBand.count,
          null,
          firstBand.max,
        ),
      );
    }

    const year = visible('year');
    if (year && ['year-list', 'year-chips'].includes(year.control)) {
      const [newest] = year.getYears();
      if (newest && year.getYears().length > 1) {
        pills.push(rangePill(year, String(newest), undefined, String(newest), String(newest)));
      }
    }

    const odometer = visible('odometer');
    if (odometer && odometer.getOdometerSteps().includes(50000)) {
      pills.push(
        rangePill(
          odometer,
          sprintf(i18n.under, `${formatNumber(50000)} ${i18n.km}`),
          undefined,
          null,
          '50000',
        ),
      );
    }

    const fuel = visible('fuel_type');
    if (fuel) {
      options(fuel)
        .filter((option) => ['electric', 'hybrid', 'plug-in-hybrid'].includes(option.key))
        .forEach((option) => pills.push(listPill(fuel, option)));
    }

    const category = visible('category');
    if (category) {
      options(category)
        .slice(0, 2)
        .forEach((option) => pills.push(listPill(category, option)));
    }

    return pills.slice(0, 7);
  }

  function renderQuickFilters(params) {
    if (!quickFilters) {
      return;
    }

    const pills = quickFilterPills(params);
    quickFilters.innerHTML = pills
      .map(
        (pill, index) => `<button type="button" class="srp-quick__pill" data-pill="${index}"
          aria-pressed="${pill.active}">
          <span>${escapeHtml(pill.label)}</span>
          ${pill.count !== undefined ? `<span class="srp-quick__count">${formatNumber(pill.count)}</span>` : ''}
        </button>`,
      )
      .join('');

    quickFilters.querySelectorAll('[data-pill]').forEach((button) => {
      button.addEventListener('click', () => {
        button.disabled = true;
        pills[Number(button.dataset.pill)].toggle();
      });
    });

    updateQuickFade();
  }

  /**
   * On small screens the quick filters scroll sideways in one row. Fade the
   * right edge while there are more off screen, so it's clear the row scrolls.
   */
  function updateQuickFade() {
    if (quickRow) {
      const hidden = quickRow.scrollWidth - quickRow.clientWidth - quickRow.scrollLeft;
      quickRow.classList.toggle('has-more', hidden > 4);
    }
  }
  quickRow?.addEventListener('scroll', updateQuickFade, { passive: true });
  window.addEventListener('resize', updateQuickFade, { passive: true });

  /*
  |--------------------------------------------------------------------------
  | Deals counts
  |--------------------------------------------------------------------------
  |
  | The API has no facet for "on sale" or "any savings" yet, so count them with
  | one-vehicle searches through the same widget the plugin uses. Remove this
  | once the API returns those counts with the search.
  |
  */
  function fetchDealCounts(params) {
    const deals = controls.get('deals');
    if (!deals) {
      return;
    }

    const base = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value && !COUNT_IGNORED_PARAMS.includes(key)) {
        base.set(`dsp_${key}`, value);
      }
    });
    const selected = parseJson(dspFilters.dataset.selected);
    LOCKED_PARAMS.forEach((key) => {
      const value = selected[key];
      if (value && !base.has(`dsp_${key}`)) {
        base.set(`dsp_${key}`, Array.isArray(value) ? value.join(',') : value);
      }
    });
    const customFilter = document.querySelector('[data-custom-filter]')?.dataset.customFilter;
    const page = document.querySelector('[data-page]')?.dataset.page;
    const lang = new URL(window.location).searchParams.get('lang');
    if (customFilter) base.set('custom_filter', customFilter);
    if (page) base.set('wp_page', page);
    if (lang) base.set('lang', lang);
    base.set('dsp_limit', '1');

    // Sorting, paging and redraws don't change the counts.
    if (base.toString() === dealCountQuery) {
      return;
    }
    dealCountQuery = base.toString();

    dealCountRequest?.abort();
    dealCountRequest = new AbortController();
    const { signal } = dealCountRequest;

    const count = async (key) => {
      const query = new URLSearchParams(base);
      query.set(`dsp_${key}`, 'true');
      const response = await fetch(`/widget/search-results?${query}`, {
        signal,
        headers: { 'X-Search-Request': 'true' },
      });
      const html = await response.text();
      const match = html.match(/vehicle-results-counts[^>]*data-total="(\d+)"/);
      return [key, match ? Number(match[1]) : undefined];
    };

    Promise.all(['on_sale', 'has_discounted_price'].map(count))
      .then((entries) => {
        deals.setDealCounts(Object.fromEntries(entries));
        renderQuickFilters(getParams());
        renderGroupNav();
      })
      .catch(() => {
        // Aborted by a newer search, or offline: keep the options without counts,
        // and let the next search try again.
        dealCountQuery = '';
      });
  }

  /*
  |--------------------------------------------------------------------------
  | Analytics
  |--------------------------------------------------------------------------
  |
  | Same Adobe payload as the legacy checkbox filters in quick-filters.js.
  |
  */
  function trackChange(control, detail, previous) {
    const status = $('.search-results').attr('data-sale-status');
    const nameplates = ['make', 'model'];
    let filterType = control.param;
    if (filterType === 'engine_type') {
      filterType = 'engine';
    } else if (filterType === 'odometer') {
      filterType = 'mileage';
    }

    const config = {
      onclick: {
        onclick: `${status}:si:vls:filter:${filterType}`,
        onclickLinkName: `dc:${status}:si:vls:content:action:filter`,
      },
    };

    if (nameplates.includes(control.param) && detail.value) {
      const before = splitList(previous);
      const added = splitList(detail.value).filter((value) => !before.includes(value));
      if (!added.length) {
        return;
      }
      config.vehicle = { nameplate: control.labelFor(added[added.length - 1]) };
    } else if (control.param === 'year' && detail.min && detail.min === detail.max) {
      config.vehicle = { modelYear: String(detail.min) };
    }

    try {
      TagManager.trackEvent('global-onclick', config);
    } catch (e) {
      // Tracking must never block filtering.
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Filter sheet (small screens)
  |--------------------------------------------------------------------------
  |
  | Amazon's two-pane sheet: filter groups down the left with their selected
  | counts, the chosen group's options on the right.
  |
  */
  function renderGroupNav() {
    if (!groupNav) {
      return;
    }

    const sections = Array.from(panel.querySelectorAll('[data-filter-section]')).filter(
      (section) => !section.hidden,
    );
    if (!sections.some((section) => section.dataset.filterSection === activeGroup)) {
      activeGroup = sections[0]?.dataset.filterSection ?? null;
    }

    const hadFocus = groupNav.contains(document.activeElement)
      ? document.activeElement.dataset.group
      : null;

    groupNav.innerHTML = sections
      .map((section) => {
        const id = section.dataset.filterSection;
        const control = controls.get(id);
        const count = control?.selectedCount() ?? 0;
        return `<li><button type="button" class="srp-filter-panel__group" data-group="${escapeHtml(id)}"
            aria-controls="srp-filter-${escapeHtml(id)}" aria-current="${id === activeGroup}">
            <span>${escapeHtml(control?.label ?? id)}</span>
            ${count ? `<span class="srp-filter-panel__group-count">${count}</span>` : ''}
          </button></li>`;
      })
      .join('');

    panel.querySelectorAll('[data-filter-section]').forEach((section) => {
      section.classList.toggle('is-active', section.dataset.filterSection === activeGroup);
    });

    if (hadFocus) {
      groupNav.querySelector(`[data-group="${CSS.escape(hadFocus)}"]`)?.focus();
    }
  }

  groupNav?.addEventListener('click', (event) => {
    const button = event.target.closest('[data-group]');
    if (button) {
      activeGroup = button.dataset.group;
      renderGroupNav();
    }
  });

  let lastTrigger = null;

  function openPanel(trigger) {
    if (!panel) {
      return;
    }
    lastTrigger = trigger ?? null;
    root.classList.add('is-panel-open');
    document.body.classList.add('srp-filter-panel-open');
    root.querySelectorAll('.js-srp-filters-open').forEach((button) => {
      button.setAttribute('aria-expanded', 'true');
    });
    panel.querySelector('.srp-filter-panel__close')?.focus();
  }

  function closePanel() {
    if (!root.classList.contains('is-panel-open')) {
      return;
    }
    root.classList.remove('is-panel-open');
    document.body.classList.remove('srp-filter-panel-open');
    root.querySelectorAll('.js-srp-filters-open').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
    lastTrigger?.focus();
  }

  root.querySelectorAll('.js-srp-filters-open').forEach((button) => {
    button.addEventListener('click', () => openPanel(button));
  });
  root.querySelectorAll('.js-srp-filters-close').forEach((button) => {
    button.addEventListener('click', closePanel);
  });
  document.addEventListener('keydown', (event) => {
    if (!root.classList.contains('is-panel-open')) {
      return;
    }
    if (event.key === 'Escape') {
      closePanel();
    } else if (event.key === 'Tab') {
      trapFocus(event);
    }
  });

  // Keep keyboard focus inside the open panel.
  function trapFocus(event) {
    const focusable = Array.from(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select'),
    ).filter((el) => el.offsetParent !== null);
    if (!focusable.length) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Wiring
  |--------------------------------------------------------------------------
  */
  dspFilters.querySelectorAll('.filter-container[data-control]').forEach((container) => {
    const control = new FilterControl(container, i18n, trackChange);
    controls.set(control.param, control);
  });

  // Trim and Packages show inside the option chosen in their parent filter.
  controls.forEach((control) => {
    const parent = controls.get(control.nest);
    if (parent) {
      control.nestUnder(parent);
    }
  });

  // First paint straight away, whatever order the plugin and theme scripts load in.
  renderAll();

  // The plugin rebuilds filters after a drilldown change, but not after a range
  // or sort change, so redraw from every fetch once the URL has been updated.
  $(document).on(InventorySearchEvents.PRE_FETCH, () => {
    root.classList.add('is-loading');
  });

  $(document).on(InventorySearchEvents.POST_FETCH, (event, data) => {
    hasSearched = true;
    if (data) {
      inventoryData = data;
    }
    window.setTimeout(renderAll, 0);
  });

  $(document).on(InventorySearchEvents.POST_RENDER, (event, vehicleContainer) => {
    root.classList.remove('is-loading');
    const total = $(vehicleContainer).find('.vehicle-results-counts').attr('data-total');
    if (total !== undefined) {
      root.querySelectorAll('.js-srp-total').forEach((el) => {
        el.textContent = total;
      });
      if (resultsStatus) {
        resultsStatus.textContent = sprintf(i18n.resultsStatus, formatNumber(total));
      }

      // The new total can change whether site-wide counts (Body Style) are right.
      const params = { ...getParams(), ...pendingParams() };
      controls.forEach((control) => {
        if (control.countsUnfiltered && control.resultTotal !== Number(total)) {
          control.resultTotal = Number(total);
          control.render(inventoryData, params);
        }
      });
      renderQuickFilters(params);
    }
  });
}

function parseJson(value) {
  try {
    return JSON.parse(value || '{}') ?? {};
  } catch (e) {
    return {};
  }
}
